'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion, Variants, AnimatePresence, useReducedMotion } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaOptionsType } from 'embla-carousel';
import Autoplay from 'embla-carousel-autoplay';
import { Button, Badge, Text, Heading } from '@/components/ui/design-system';
import { ArrowRight, Pause, Play, ChevronsDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { slides } from '@/data/homeHeroSlides';
import { useMediaQuery } from '@/hooks/useMediaQuery';

// --- Constants ---
const AUTOPLAY_DELAY = 5000; // in milliseconds
const CAROUSEL_TRANSITION_DURATION = 50; // in milliseconds

// --- Animation Variants - Memoized for performance ---
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
} as const;

const wordVariants: Variants = {
  hidden: { opacity: 0, y: 20, rotateX: -30, transformOrigin: 'bottom' },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: { type: 'spring', damping: 12, stiffness: 100 },
  },
} as const;

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
} as const;

// --- Sub-components ---

/**
 * Renders an animated heading with kinetic typography effects.
 * Gracefully degrades to a static heading if the user prefers reduced motion.
 * @param {object} props - The component props.
 * @param {string} props.text - The text to display in the heading.
 */
const KineticHeading = React.memo(({ text }: { text: string }) => {
  const shouldReduceMotion = useReducedMotion();
  const words = useMemo(() => text.split(' '), [text]);

  if (shouldReduceMotion) {
    return (
      <Heading level={2} align="left" className="mb-3 text-5xl" style="gradient">
        {text}
      </Heading>
    );
  }

  return (
    <Heading level={2} align="left" className="mb-3 text-5xl" style="gradient">
      <span className="sr-only">{text}</span>
      <motion.span
        aria-hidden
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {words.map((word, index) => (
          <motion.span key={`${word}-${index}`} className="inline-block mr-4" variants={wordVariants}>
            {word}
          </motion.span>
        ))}
      </motion.span>
    </Heading>
  );
});

/**
 * Renders an animated scroll-down indicator to guide the user.
 * Animations are disabled if the user prefers reduced motion.
 */
const ScrollDownIcon = React.memo(() => {
  const shouldReduceMotion = useReducedMotion();

  const handleScrollClick = useCallback(() => {
    const nextSection = document.getElementById('product-showcase');
    if (nextSection) {
      window.scrollTo({ top: nextSection.offsetTop, behavior: 'smooth' });
    }
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 2, duration: 1, ease: 'easeInOut' }}
      className="absolute bottom-20 sm:bottom-12 left-1/2 -translate-x-1/2 z-20"
    >
      <button
        onClick={handleScrollClick}
        className="group transition-transform duration-300 ease-out hover:scale-110 focus:outline-none"
        aria-label="Scroll to next section"
      >
        <div className="w-10 h-16 sm:w-11 sm:h-20 rounded-full border-2 border-neutral-800 dark:border-white/60 group-hover:border-neutral-900 dark:group-hover:border-white transition-colors duration-300 flex flex-col justify-start items-center pt-3 space-y-2 overflow-hidden">
          <motion.div
            className="w-2 h-2 rounded-full bg-neutral-800 dark:bg-white/60 group-hover:bg-neutral-900 dark:group-hover:bg-white"
            animate={{ y: shouldReduceMotion ? 0 : [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, repeatType: 'loop', ease: 'easeInOut' }}
          />
          <motion.div
            whileHover={shouldReduceMotion ? {} : { y: -5 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronsDown className="w-5 h-5 text-neutral-800 dark:text-white/60 group-hover:text-neutral-900 dark:group-hover:text-white" />
          </motion.div>
        </div>
      </button>
    </motion.div>
  );
});

// --- Main Component ---

/**
 * Renders the main hero section for the homepage.
 * Features a full-screen auto-playing carousel with kinetic typography and enhanced micro-interactions.
 * It is optimized for performance, SEO, and accessibility, including support for reduced motion.
 */
export const HomeHero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useMediaQuery('(max-width: 1024px)');

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const shouldReduceMotion = isMounted && prefersReducedMotion;

  // Memoize autoplay configuration to prevent recreation
  const autoplayConfig = useMemo(() => ({
    delay: AUTOPLAY_DELAY,
    stopOnInteraction: false, // Allow autoplay to resume after interaction
    stopOnFocusIn: false, // Prevent stopping on focus
  }), []);

  const autoplay = useRef(Autoplay(autoplayConfig));

  // Memoize carousel configuration
  const carouselConfig = useMemo<EmblaOptionsType>(() => ({
    loop: true,
    duration: shouldReduceMotion ? 0 : CAROUSEL_TRANSITION_DURATION,
    slidesToScroll: 1,
    containScroll: 'trimSnaps'
  }), [shouldReduceMotion]);

  const [emblaRef, emblaApi] = useEmblaCarousel(carouselConfig, [autoplay.current]);

  // Update playing state based on reduced motion preference
  useEffect(() => {
    setIsPlaying(!shouldReduceMotion);
  }, [shouldReduceMotion]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const togglePlay = useCallback(() => {
    const autoplayPlugin = autoplay.current;
    if (!autoplayPlugin || shouldReduceMotion) return;

    if (autoplayPlugin.isPlaying()) {
      autoplayPlugin.stop();
      setIsPlaying(false);
    } else {
      autoplayPlugin.play();
      setIsPlaying(true);
    }
  }, [shouldReduceMotion]);

  useEffect(() => {
    if (!emblaApi) return;
    
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    
    emblaApi.on('select', onSelect);
    
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, shouldReduceMotion]);

  if (!isMounted) {
    return null; // Or a loading skeleton
  }

  return (
    <section className="relative h-screen w-full overflow-hidden bg-black">
      {/* Carousel Viewport */}
      <div className="absolute inset-0 z-0" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, index) => (
            <div className="relative flex-[0_0_100%] h-full" key={slide.id}>
              {slide.type === 'image' ? (
                <Image
                  src={isMobile && slide.srcMobile ? slide.srcMobile : slide.src}
                  alt={slide.alt}
                  fill
                  priority={index === 0}
                  className="object-cover"
                  sizes="100vw"
                  quality={index === 0 ? 90 : 85}
                  placeholder="blur"
                  blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R+Rw="
                />
              ) : (
                <video
                  src={slide.src}
                  className="h-full w-full object-cover"
                  autoPlay={!shouldReduceMotion}
                  loop
                  muted
                  playsInline
                  preload={index === 0 ? "auto" : "metadata"}
                />
              )}
              <div className="absolute inset-0 dark:bg-gradient-to-t dark:from-black/70 dark:via-black/40 dark:to-transparent" />
            </div>
          ))}
        </div>
      </div>

      {/* Text content overlay */}
      <div className="absolute inset-0 z-10 p-6 pt-24 sm:p-12 md:p-16 lg:p-24 pointer-events-none">
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedIndex}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={containerVariants}
            className="w-full h-full flex flex-col justify-between lg:grid lg:grid-cols-12 lg:items-center lg:justify-start"
          >
            {/* Top Content (Mobile) / Main Content (Desktop) */}
            <div className="lg:col-span-6 lg:max-w-xl">
              <motion.div variants={itemVariants}>
                <Badge variant="premium" size="lg" radius="full" className="mb-4 pointer-events-auto">
                  {slides[selectedIndex]?.topline}
                </Badge>
              </motion.div>
              <Heading level={2} align="left" className="text-3xl md:text-5xl lg:text-5xl text-slate-900 dark:text-white">
                {slides[selectedIndex]?.title || ''}
              </Heading>
              <motion.div variants={itemVariants} className="hidden lg:block">
                <Text
                  variant="body-large"
                  className="text-base md:text-lg mt-2 md:mt-4 max-w-xl font-normal leading-relaxed text-slate-700 dark:text-gray-300"
                >
                  {slides[selectedIndex]?.subtitle}
                </Text>
              </motion.div>

              {/* Buttons - part of main content on desktop, separate on mobile */}
              <motion.div
                variants={itemVariants}
                className="hidden lg:flex flex-col sm:flex-row gap-4 mt-8 pointer-events-auto"
              >
                <Link href={slides[selectedIndex]?.primaryCtaLink || '#'} className="w-full sm:w-auto">
                  <Button
                    variant="primary"
                    size="lg"
                    className="group relative w-full whitespace-nowrap overflow-hidden rounded-full"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent transition-transform duration-500 ease-in-out -translate-x-full group-hover:translate-x-0" />
                    <span className="relative">{slides[selectedIndex]?.primaryCta}</span>
                    <motion.div
                      className="relative ml-2"
                      whileHover={shouldReduceMotion ? undefined : { x: 4 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <ArrowRight className="h-4 w-4" />
                    </motion.div>
                  </Button>
                </Link>
                <Link href={slides[selectedIndex]?.secondaryCtaLink || '#'} className="w-full sm:w-auto">
                  <Button
                    variant="secondary"
                    size="lg"
                    className="w-full whitespace-nowrap rounded-full"
                  >
                    {slides[selectedIndex]?.secondaryCta}
                  </Button>
                </Link>
              </motion.div>
            </div>

            {/* Bottom Content: Buttons (Mobile/Tablet Only) */}
            <motion.div
              variants={itemVariants}
              className="flex lg:hidden flex-col sm:flex-row gap-4 mt-8 pointer-events-auto"
            >
              <Link href={slides[selectedIndex]?.primaryCtaLink || '#'} className="w-full sm:w-auto">
                <Button
                  variant="primary"
                  size="lg"
                  className="group relative w-full whitespace-nowrap overflow-hidden rounded-full"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent transition-transform duration-500 ease-in-out -translate-x-full group-hover:translate-x-0" />
                  <span className="relative">{slides[selectedIndex]?.primaryCta}</span>
                  <motion.div
                    className="relative ml-2"
                    whileHover={shouldReduceMotion ? undefined : { x: 4 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <ArrowRight className="h-4 w-4" />
                  </motion.div>
                </Button>
              </Link>
              <Link href={slides[selectedIndex]?.secondaryCtaLink || '#'} className="w-full sm:w-auto">
                <Button
                  variant="secondary"
                  size="lg"
                  className="w-full whitespace-nowrap rounded-full"
                >
                  {slides[selectedIndex]?.secondaryCta}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Carousel Controls */}
      {!shouldReduceMotion && (
        <>
          {/* Navigation Arrows (Desktop Only) */}
          <button
            onClick={scrollPrev}
            aria-label="Previous slide"
            className="hidden lg:flex absolute left-8 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors"
          >
            <ChevronLeft className="h-6 w-6 text-white" />
          </button>
          <button
            onClick={scrollNext}
            aria-label="Next slide"
            className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors"
          >
            <ChevronRight className="h-6 w-6 text-white" />
          </button>

        </>
      )}

      {!isMobile && <ScrollDownIcon />}
    </section>
  );
};
